import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Users, Baby, LogIn, LogOut } from "lucide-react";
import { toast } from "react-hot-toast";
import { housePresenceKeys, houseService } from "@/services/houseService";
import { deviceKeys } from "@/services/deviceService";
import { Badge, Button, Card, CardContent, ConfirmModal, Checkbox, Input, Label, Modal, Textarea, EmptyState } from "@/components/ui";
import { CountrySelect } from "@/components/ui/phone";
import { useCountryOptions } from "@/hooks/useCountryOptions";

import { DetailSectionTitle } from "@/components/ui";
import {
  clampPresencePeopleInput,
  padTimeString,
  parseUtcDateTime,
  PRESENCE_PEOPLE_MAX,
  PRESENCE_PEOPLE_MIN,
} from "@/lib/devicePresence";
import { toastApiError } from "@/lib/errorMessages";
import { presenceGuestDisplayName } from "@/lib/presenceDisplay";
import type {
  DevicePresence,
  DevicePresenceCreate,
  DevicePresenceUpdate,
  HouseDevice,
} from "@/types";

interface DevicePresencePanelProps {
  isOpen: boolean;
  onClose: () => void;
  houseId: string;
  kiosk: HouseDevice;
}

function invalidatePresenceQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  houseId: string,
  deviceId: string,
) {
  void Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["kiosk-presences", houseId, deviceId],
    }),
    queryClient.invalidateQueries({ queryKey: deviceKeys.byHouse(houseId) }),
    queryClient.invalidateQueries({
      queryKey: housePresenceKeys.current(houseId),
    }),
    queryClient.invalidateQueries({
      queryKey: housePresenceKeys.upcoming(houseId),
    }),
    queryClient.invalidateQueries({
      queryKey: housePresenceKeys.history(houseId),
    }),
  ]);
}

const DEFAULT_PRESENCE_PEOPLE = String(PRESENCE_PEOPLE_MIN);

function PresenceCard({
  presence,
  onEdit,
  onDelete,
  variant,
}: {
  presence: DevicePresence;
  onEdit: () => void;
  onDelete: () => void;
  variant: "current" | "future";
}) {
  const { t } = useTranslation("houses");
  const { t: tCommon } = useTranslation("common");
  const genericGuestName = t("detail.presences.genericGuestName");
  const arrTime = padTimeString(presence.arrival_time);
  const depTime = padTimeString(presence.departure_time);
  const variantStyles = {
    current: "border-success-200 bg-success-50/60",
    future: "border-surface-200 bg-surface-0",
  };

  return (
    <div
      className={`rounded-lg border px-4 py-3 ${variantStyles[variant]}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-surface-900">
            {presenceGuestDisplayName(presence.guest_name, genericGuestName)}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-surface-600">
            {presence.nationality && (
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {presence.nationality}
              </span>
            )}
            <span>
              {t("kioskPresence.peopleCount", {
                count: presence.num_people,
              })}
            </span>
            {presence.has_children ? (
              <Badge variant="warning" size="sm">
                <Baby />
                {t("kioskPresence.childrenBadge")}
              </Badge>
            ) : null}
            {presence.marketing_consent ? (
              <Badge variant="success" size="sm">
                {t("kioskPresence.consentRecordedBadge")}
              </Badge>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-success-50 px-2 py-1 text-success-800">
              <LogIn className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="font-medium">{presence.arrival_date}</span>
              <span className="tabular-nums text-success-700/80">{arrTime}</span>
            </span>
            <span className="text-surface-300" aria-hidden>
              →
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-md bg-warning-50 px-2 py-1 text-warning-800">
              <LogOut className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="font-medium">{presence.departure_date}</span>
              <span className="tabular-nums text-warning-700/80">{depTime}</span>
            </span>
          </div>
          {presence.notes && (
            <p className="mt-2 text-xs text-surface-500">{presence.notes}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={onEdit}
            title={tCommon("edit")}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 text-error-600 hover:bg-error-50 hover:text-error-700 dark:hover:text-error-800"
            onClick={onDelete}
            title={tCommon("delete")}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function getDefaultDepartureDate(): string {
  const fourDaysFromNow = new Date();
  fourDaysFromNow.setDate(fourDaysFromNow.getDate() + 4);
  return fourDaysFromNow.toISOString().split("T")[0];
}

function getTomorrowDate(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split("T")[0];
}

const DEFAULT_SCHEDULED_ARRIVAL_TIME = "15:00";

export function DevicePresencePanel({
  isOpen,
  onClose,
  houseId,
  kiosk,
}: DevicePresencePanelProps) {
  const { t: tComponents } = useTranslation("components");
  const { t } = useTranslation("houses");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const countryOptions = useCountryOptions();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPresence, setEditingPresence] = useState<DevicePresence | null>(
    null,
  );

  // Form state
  const [guestName, setGuestName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [nationality, setNationality] = useState<string>(kiosk.region_code);
  const [arrivalDate, setArrivalDate] = useState("");
  const [arrivalTime, setArrivalTime] = useState("00:00");
  const [scheduleLaterArrival, setScheduleLaterArrival] = useState(false);
  const [departureDate, setDepartureDate] = useState(getDefaultDepartureDate());
  const [departureTime, setDepartureTime] = useState("10:00");
  const [numPeopleInput, setNumPeopleInput] = useState(DEFAULT_PRESENCE_PEOPLE);
  const [hasChildren, setHasChildren] = useState(false);
  const [notes, setNotes] = useState("");
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [consentProfiling, setConsentProfiling] = useState(false);
  const [consentThirdParty, setConsentThirdParty] = useState(false);
  const [pendingCreateData, setPendingCreateData] =
    useState<DevicePresenceCreate | null>(null);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<DevicePresence | null>(
    null,
  );

  const queryKey = (filter: "current" | "future") =>
    ["kiosk-presences", houseId, kiosk.id, filter] as const;

  const { data: currentPresence = [] } = useQuery({
    queryKey: queryKey("current"),
    queryFn: () =>
      houseService.listDevicePresences(houseId, kiosk.id, "current"),
    enabled: isOpen && !!houseId && !!kiosk.id,
  });

  const { data: futurePresences = [] } = useQuery({
    queryKey: queryKey("future"),
    queryFn: () => houseService.listDevicePresences(houseId, kiosk.id, "future"),
    enabled: isOpen && !!houseId && !!kiosk.id,
  });

  const createMutation = useMutation({
    mutationFn: ({
      data,
      replaceCurrent,
    }: {
      data: DevicePresenceCreate;
      replaceCurrent?: boolean;
    }) =>
      houseService.createDevicePresence(houseId, kiosk.id, data, {
        replaceCurrent,
      }),
    onSuccess: () => {
      invalidatePresenceQueries(queryClient, houseId, kiosk.id);
      toast.success(t("kioskPresence.toastAdded"));
      resetForm();
      setShowAddForm(false);
      setPendingCreateData(null);
      setShowReplaceConfirm(false);
    },
    onError: (err: unknown) => {
      toastApiError(err, "addPresence");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      presenceId,
      data,
    }: {
      presenceId: string;
      data: DevicePresenceUpdate;
    }) => houseService.updateDevicePresence(houseId, kiosk.id, presenceId, data),
    onSuccess: () => {
      invalidatePresenceQueries(queryClient, houseId, kiosk.id);
      toast.success(t("kioskPresence.toastUpdated"));
      resetForm();
      setEditingPresence(null);
    },
    onError: (err: unknown) => {
      toastApiError(err, "updatePresence");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (presenceId: string) =>
      houseService.deleteDevicePresence(houseId, kiosk.id, presenceId),
    onSuccess: () => {
      invalidatePresenceQueries(queryClient, houseId, kiosk.id);
      setPendingDelete(null);
      toast.success(t("kioskPresence.toastDeleted"));
    },
    onError: (err) => {
      toastApiError(err, "deletePresence");
    },
  });

  const resetForm = () => {
    setGuestName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setNationality(kiosk.region_code);
    setArrivalDate("");
    setArrivalTime("00:00");
    setScheduleLaterArrival(false);
    setDepartureDate(getDefaultDepartureDate());
    setDepartureTime("10:00");
    setNumPeopleInput(DEFAULT_PRESENCE_PEOPLE);
    setHasChildren(false);
    setNotes("");
    setConsentMarketing(false);
    setConsentProfiling(false);
    setConsentThirdParty(false);
  };

  const openEditForm = (p: DevicePresence) => {
    setEditingPresence(p);
    setShowAddForm(false);
    setScheduleLaterArrival(false);
    setGuestName(p.guest_name?.trim() ?? "");
    setLastName("");
    setNationality(p.nationality || kiosk.region_code);
    setArrivalDate(p.arrival_date);
    setArrivalTime(padTimeString(p.arrival_time || "00:00"));
    setDepartureDate(p.departure_date);
    setDepartureTime(padTimeString(p.departure_time || "23:59"));
    setNumPeopleInput(String(p.num_people));
    setHasChildren(p.has_children);
    setNotes(p.notes || "");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedGuestName = guestName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedNationality = nationality.trim();

    if (!trimmedGuestName) {
      toast.error(t("kioskPresence.errorGuestNameRequired"));
      return;
    }
    if (!trimmedNationality) {
      toast.error(t("kioskPresence.errorNationalityRequired"));
      return;
    }
    if (editingPresence && !arrivalDate) {
      toast.error(t("kioskPresence.errorDatesRequired"));
      return;
    }
    if (!editingPresence && scheduleLaterArrival && !arrivalDate) {
      toast.error(t("kioskPresence.errorDatesRequired"));
      return;
    }
    if (!departureDate) {
      toast.error(t("kioskPresence.errorDepartureDateRequired"));
      return;
    }
    const usesScheduledArrival =
      !editingPresence && scheduleLaterArrival && Boolean(arrivalDate);
    const arrDt =
      editingPresence || usesScheduledArrival
        ? parseUtcDateTime(arrivalDate, arrivalTime)
        : new Date();
    const depDt = parseUtcDateTime(departureDate, departureTime);
    if (depDt <= arrDt) {
      toast.error(t("kioskPresence.errorDepartureAfterArrival"));
      return;
    }

    const hasAnyConsent =
      consentMarketing || consentProfiling || consentThirdParty;

    const presenceData = {
      guest_name: trimmedGuestName,
      nationality: trimmedNationality,
      departure_date: departureDate,
      departure_time: departureTime,
      num_people: parseInt(clampPresencePeopleInput(numPeopleInput), 10),
      has_children: hasChildren,
      notes: notes.trim() || undefined,
    };

    if (editingPresence) {
      const data: DevicePresenceUpdate = {
        ...presenceData,
        arrival_date: arrivalDate,
        arrival_time: arrivalTime,
      };
      updateMutation.mutate({ presenceId: editingPresence.id, data });
    } else {
      const data: DevicePresenceCreate = {
        ...presenceData,
        ...(trimmedLastName ? { last_name: trimmedLastName } : {}),
        ...(usesScheduledArrival
          ? { arrival_date: arrivalDate, arrival_time: arrivalTime }
          : {}),
      };
      if (hasAnyConsent) {
        const trimmedEmail = email.trim();
        const trimmedPhone = phone.trim();
        if (trimmedEmail) data.email = trimmedEmail;
        if (trimmedPhone) data.phone = trimmedPhone;
        data.consent_marketing = consentMarketing;
        data.consent_profiling = consentProfiling;
        data.consent_third_party = consentThirdParty;
      }
      // Replace dialog only for immediate check-in over an active guest.
      if (currentPresence[0] && !usesScheduledArrival) {
        setPendingCreateData(data);
        setShowReplaceConfirm(true);
      } else {
        createMutation.mutate({ data });
      }
    }
  };

  const handleConfirmReplace = () => {
    if (!pendingCreateData) return;
    createMutation.mutate({ data: pendingCreateData, replaceCurrent: true });
  };

  const handleDelete = (p: DevicePresence) => {
    setPendingDelete(p);
  };

  const current = currentPresence[0] ?? null;

  const displayKioskName =
    kiosk.display_name.trim() || t("kioskPresence.tabletFallback");
  const presenceModalTitle = t("kioskPresence.modalTitle", {
    kioskName: displayKioskName,
  });
  const presenceModalDescription = kiosk.kiosk_code.trim() || undefined;

  return (
    <>
    <ConfirmModal
      closeLabel={tCommon("close")}
      cancelText={tCommon("cancel")}
     
      isOpen={showReplaceConfirm}
      onClose={() => {
        if (createMutation.isPending) return;
        setShowReplaceConfirm(false);
        setPendingCreateData(null);
      }}
      onConfirm={handleConfirmReplace}
      title={t("kioskPresence.replaceCurrentTitle")}
      message={t("kioskPresence.replaceCurrentMessage", {
        guestName: presenceGuestDisplayName(
          current?.guest_name,
          t("detail.presences.genericGuestName"),
        ),
      })}
      confirmText={t("kioskPresence.replaceCurrentConfirm")}
      isLoading={createMutation.isPending}
    />
    <ConfirmModal
      closeLabel={tCommon("close")}
      cancelText={tCommon("cancel")}
     
      isOpen={pendingDelete != null}
      onClose={() => {
        if (deleteMutation.isPending) return;
        setPendingDelete(null);
      }}
      onConfirm={() => {
        if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
      }}
      title={tCommon("delete")}
      message={t("kioskPresence.deleteConfirm", {
        guestName: presenceGuestDisplayName(
          pendingDelete?.guest_name,
          t("detail.presences.genericGuestName"),
        ),
      })}
      confirmText={tCommon("delete")}
        isDestructive
      isLoading={deleteMutation.isPending}
    />
    <Modal
      closeLabel={tCommon("close")}
      isOpen={isOpen}
      onClose={() => {
        resetForm();
        setShowAddForm(false);
        setEditingPresence(null);
        setPendingCreateData(null);
        setShowReplaceConfirm(false);
        setPendingDelete(null);
        onClose();
      }}
      title={presenceModalTitle}
      description={presenceModalDescription}
      size="lg"
    >
      <div className="space-y-6">
        {/* Current Guest */}
        <section>
          <DetailSectionTitle as="h3" className="mb-2 text-sm font-semibold text-surface-900">
            {t("kioskPresence.sectionCurrentGuest")}
          </DetailSectionTitle>
          {current ? (
            <PresenceCard
              presence={current}
              onEdit={() => openEditForm(current)}
              onDelete={() => handleDelete(current)}
              variant="current"
            />
          ) : (
            <EmptyState
              variant="dashed"
              size="sm"
              showIcon={false}
              title={t("kioskPresence.emptyNoCurrentGuest")}
              className="rounded-lg bg-surface-50/50"
            />
          )}
        </section>

        {/* Upcoming Presences */}
        <section>
          <div className="mb-2 flex items-center justify-between">
            <DetailSectionTitle as="h3" className="text-sm font-semibold text-surface-900">
              {t("kioskPresence.sectionUpcoming")}
            </DetailSectionTitle>
            {!showAddForm && !editingPresence && (
              <Button
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => {
                  resetForm();
                  setShowAddForm(true);
                }}
              >
                {t("kioskPresence.ctaAddPresence")}
              </Button>
            )}
          </div>

          {(showAddForm || editingPresence) && (
            <Card className="mb-4">
              <CardContent className="p-4">
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="guestName">
                        {t("kioskPresence.labelGuestName")}
                      </Label>
                      <Input
                        id="guestName"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        placeholder={t("kioskPresence.placeholderGuestName")}
                        required
                      />
                    </div>
                    {!editingPresence && (
                      <div>
                        <Label htmlFor="lastName">
                          {t("kioskPresence.labelGuestLastName")}
                        </Label>
                        <Input
                          id="lastName"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder={t(
                            "kioskPresence.placeholderGuestLastName",
                          )}
                        />
                      </div>
                    )}
                    <div>
                      <Label htmlFor="nationality">
                        {t("kioskPresence.labelNationality")}
                      </Label>
                      <CountrySelect
                        searchPlaceholderLabel={tComponents("countrySelectSearchPlaceholder")}
                        searchInputAriaLabel={tComponents("countrySelectSearchPlaceholder")}
                        noResultsLabel={tComponents("countrySelectNoResults")}
                        id="nationality"
                        value={nationality}
                        onChange={setNationality}
                        placeholder={t("kioskPresence.placeholderNationality")}
                        countries={countryOptions}
                        defaultCountry={kiosk.region_code}
                      />
                    </div>
                  </div>
                  {!editingPresence && (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="guestEmail">
                          {t("kioskPresence.labelGuestEmail")}
                        </Label>
                        <Input
                          id="guestEmail"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={t(
                            "kioskPresence.placeholderGuestEmail",
                          )}
                        />
                      </div>
                      <div>
                        <Label htmlFor="guestPhone">
                          {t("kioskPresence.labelGuestPhone")}
                        </Label>
                        <Input
                          id="guestPhone"
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder={t(
                            "kioskPresence.placeholderGuestPhone",
                          )}
                        />
                      </div>
                    </div>
                  )}
                  <div className="grid gap-4 sm:grid-cols-2">
                    {!editingPresence && (
                      <div className="sm:col-span-2">
                        <Label
                          spacing="none"
                          className="flex items-start gap-2 font-normal text-surface-700"
                        >
                          <Checkbox
                            className="mt-0.5"
                            id="scheduleLaterArrival"
                            checked={scheduleLaterArrival}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setScheduleLaterArrival(checked);
                              if (checked) {
                                setArrivalDate((current) =>
                                  current || getTomorrowDate(),
                                );
                                setArrivalTime(DEFAULT_SCHEDULED_ARRIVAL_TIME);
                              }
                            }}
                          />
                          <span>
                            {t("kioskPresence.scheduleLaterArrival")}
                          </span>
                        </Label>
                        <p className="mt-1 pl-6 text-xs text-surface-500">
                          {t("kioskPresence.scheduleLaterArrivalHint")}
                        </p>
                      </div>
                    )}
                    {(editingPresence || scheduleLaterArrival) && (
                      <>
                        <div>
                          <Label htmlFor="arrivalDate">
                            {t("kioskPresence.labelArrivalDate")}
                          </Label>
                          <Input
                            id="arrivalDate"
                            type="date"
                            value={arrivalDate}
                            onChange={(e) => setArrivalDate(e.target.value)}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="arrivalTime">
                            {t("kioskPresence.labelArrivalTime")}
                          </Label>
                          <Input
                            id="arrivalTime"
                            type="time"
                            value={arrivalTime}
                            onChange={(e) => setArrivalTime(e.target.value)}
                          />
                        </div>
                      </>
                    )}
                    <div>
                      <Label htmlFor="departureDate">
                        {t("kioskPresence.labelDepartureDate")}
                      </Label>
                      <Input
                        id="departureDate"
                        type="date"
                        value={departureDate}
                        onChange={(e) => setDepartureDate(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="departureTime">
                        {t("kioskPresence.labelDepartureTime")}
                      </Label>
                      <Input
                        id="departureTime"
                        type="time"
                        value={departureTime}
                        onChange={(e) => setDepartureTime(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label
                        htmlFor="numPeople"
                        className="text-sm"
                      >
                        {t("kioskPresence.labelPeople")}
                      </Label>
                      <Input
                        id="numPeople"
                        type="number"
                        min={PRESENCE_PEOPLE_MIN}
                        max={PRESENCE_PEOPLE_MAX}
                        value={numPeopleInput}
                        onChange={(e) => setNumPeopleInput(e.target.value)}
                        onBlur={() =>
                          setNumPeopleInput((current) =>
                            clampPresencePeopleInput(current),
                          )
                        }
                        onFocus={(e) => e.currentTarget.select()}
                        className="w-20"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="hasChildren"
                        checked={hasChildren}
                        onChange={(e) => setHasChildren(e.target.checked)}
                      />
                      <Label
                        htmlFor="hasChildren"
                        className="text-sm"
                      >
                        {t("kioskPresence.labelChildrenCheckbox")}
                      </Label>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="notes">
                      {t("kioskPresence.labelNotes")}
                    </Label>
                    <Textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={t("kioskPresence.placeholderNotes")}
                      rows={2}
                    />
                  </div>
                  {!editingPresence && (
                    <section className="space-y-3 rounded-lg border border-surface-200 bg-surface-50 px-4 py-3">
                      <div>
                        <h4 className="text-sm font-semibold text-surface-900">
                          {t("kioskPresence.consentSectionTitle")}
                        </h4>
                        <p className="mt-0.5 text-xs text-surface-500">
                          {t("kioskPresence.consentSectionHint")}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label
                          spacing="none"
                          className="flex items-start gap-2 font-normal text-surface-700"
                        >
                          <Checkbox
                            className="mt-0.5"
                            checked={consentMarketing}
                            onChange={(e) =>
                              setConsentMarketing(e.target.checked)
                            }
                          />
                          <span>{t("kioskPresence.consentMarketing")}</span>
                        </Label>
                        <Label
                          spacing="none"
                          className="flex items-start gap-2 font-normal text-surface-700"
                        >
                          <Checkbox
                            className="mt-0.5"
                            checked={consentProfiling}
                            onChange={(e) =>
                              setConsentProfiling(e.target.checked)
                            }
                          />
                          <span>{t("kioskPresence.consentProfiling")}</span>
                        </Label>
                        <Label
                          spacing="none"
                          className="flex items-start gap-2 font-normal text-surface-700"
                        >
                          <Checkbox
                            className="mt-0.5"
                            checked={consentThirdParty}
                            onChange={(e) =>
                              setConsentThirdParty(e.target.checked)
                            }
                          />
                          <span>{t("kioskPresence.consentThirdParty")}</span>
                        </Label>
                      </div>
                    </section>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        resetForm();
                        setShowAddForm(false);
                        setEditingPresence(null);
                      }}
                    >
                      {tCommon("cancel")}
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        createMutation.isPending ||
                        updateMutation.isPending ||
                        !guestName.trim() ||
                        !nationality.trim()
                      }
                    >
                      {editingPresence
                        ? t("kioskPresence.buttonUpdatePresence")
                        : t("kioskPresence.buttonAddPresence")}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {futurePresences.length === 0 && !showAddForm && !editingPresence ? (
            <EmptyState
              variant="dashed"
              size="sm"
              showIcon={false}
              title={t("kioskPresence.emptyNoUpcoming")}
              className="rounded-lg bg-surface-50/30 py-4"
            />
          ) : (
            <div className="space-y-2">
              {futurePresences
                .filter((p) => editingPresence?.id !== p.id)
                .map((p) => (
                  <PresenceCard
                    key={p.id}
                    presence={p}
                    onEdit={() => openEditForm(p)}
                    onDelete={() => handleDelete(p)}
                    variant="future"
                  />
                ))}
            </div>
          )}
        </section>
      </div>
    </Modal>
    </>
  );
}
